import React, { createContext, useContext, useMemo, useRef } from 'react'

import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import {
  type SharepointPickerStore,
  type SharepointPickerStoreState,
  createSharepointPickerStore,
} from '@/components/integrations/SharepointFilePicker/store'
import type { SharepointPickerItem } from '@/components/integrations/SharepointFilePicker/types'

import type { UUID } from '@/types/Id'

// ─── Context ─────────────────────────────────────────────────────────────────

const SharepointPickerContext = createContext<SharepointPickerStore | null>(
  null,
)

// ─── Provider ────────────────────────────────────────────────────────────────

interface SharepointPickerProviderProps {
  children: React.ReactNode
  initialItems?: SharepointPickerItem[]
  initialTagIds?: UUID[]
}

export const SharepointPickerProvider: React.FC<
  SharepointPickerProviderProps
> = ({ children, initialItems = [], initialTagIds = [] }) => {
  const storeRef = useRef<SharepointPickerStore>()

  if (!storeRef.current) {
    storeRef.current = createSharepointPickerStore(initialItems, initialTagIds)
  }

  return (
    <SharepointPickerContext.Provider value={storeRef.current}>
      {children}
    </SharepointPickerContext.Provider>
  )
}

// ─── Base hook ───────────────────────────────────────────────────────────────

const usePickerContext = () => {
  const context = useContext(SharepointPickerContext)
  if (!context) {
    throw new Error(
      'usePickerContext must be used within SharepointPickerProvider',
    )
  }
  return context
}

export const usePickerStore = <T,>(
  selector: (state: SharepointPickerStoreState) => T,
) => {
  const context = usePickerContext()
  return useStore(context, selector)
}

/** Returns the raw store for imperative access (e.g. store.getState()) */
export const usePickerStoreApi = () => {
  return usePickerContext()
}

// ─── Navigation hooks ────────────────────────────────────────────────────────

export const usePickerCurrentSiteId = () =>
  usePickerStore((s) => s.currentSite?.id ?? '')

export const usePickerCurrentSite = () => usePickerStore((s) => s.currentSite)

export const usePickerCurrentSitePath = () =>
  usePickerStore(useShallow((s) => s.currentSitePath))

export const usePickerCurrentDriveId = () =>
  usePickerStore((s) => s.currentDrive?.id ?? '')

export const usePickerCurrentDrive = () => usePickerStore((s) => s.currentDrive)

export const usePickerCurrentFolderId = () =>
  usePickerStore((s) => s.currentFolderId)

export const usePickerBreadcrumbs = () =>
  usePickerStore(useShallow((s) => s.breadcrumbs))

export const usePickerSelectDriveInSite = () =>
  usePickerStore((s) => s.selectDriveInSite)

export const usePickerClearCurrentDriveSelection = () =>
  usePickerStore((s) => s.clearCurrentDriveSelection)

export const usePickerSetCurrentSitePath = () =>
  usePickerStore((s) => s.setCurrentSitePath)

export const usePickerNavigateToFolder = () =>
  usePickerStore((s) => s.navigateToFolder)

export const usePickerNavigateToBreadcrumb = () =>
  usePickerStore((s) => s.navigateToBreadcrumb)

// ─── Selection hooks ─────────────────────────────────────────────────────────

export const usePickerSelectedItems = () =>
  usePickerStore((s) => s.selectedItems)

export const usePickerSelectedCount = () =>
  usePickerStore((s) => s.selectedCount)

export const usePickerSelectedEntries = () => {
  const selectedItems = usePickerSelectedItems()
  return useMemo(
    () =>
      Object.entries(selectedItems) as Array<[string, SharepointPickerItem]>,
    [selectedItems],
  )
}

export const usePickerToggleSelection = () =>
  usePickerStore((s) => s.toggleSelection)

export const usePickerRemoveSelectedItem = () =>
  usePickerStore((s) => s.removeSelectedItem)

export const usePickerClearSelection = () =>
  usePickerStore((s) => s.clearSelection)

export const usePickerSelectedTagIds = () =>
  usePickerStore((s) => s.selectedTagIds)

export const usePickerSetTagIds = () => usePickerStore((s) => s.setTagIds)

// ─── UI hooks ────────────────────────────────────────────────────────────────

export const usePickerNativeFallbackOpen = () =>
  usePickerStore((s) => s.nativeFallbackOpen)

export const usePickerSetNativeFallbackOpen = () =>
  usePickerStore((s) => s.setNativeFallbackOpen)

export const usePickerIsSubmitting = () => usePickerStore((s) => s.isSubmitting)

export const usePickerSetIsSubmitting = () =>
  usePickerStore((s) => s.setIsSubmitting)

// ─── Lifecycle hooks ─────────────────────────────────────────────────────────

export const usePickerReset = () => usePickerStore((s) => s.reset)
